import { cloneDeep } from "lodash";
import { type Booking } from "./bookings";
import { type BookingType } from "./bookingTypes";
import { type Customer } from "./customers";

type BookingFormVehicleBooking = {
  id: string | null; // null to create
  registration: string;
  isPrimary: boolean | null;
};

export type BookingForm = {
  bookingRef: string;
  startDate: string;
  endDate: string;
  arrivalTimeStart: string | null;
  arrivalTimeEnd: string | null;
  lateArrivalTimeStart: string | null;
  departureTimeEnd: string | null;
  arrivalTimeStartEnabled: boolean;
  arrivalTimeEndEnabled: boolean;
  lateArrivalTimeStartEnabled: boolean;
  departureTimeEndEnabled: boolean;
  siteId: string | null;
  pitchType: string | null;
  pitchNumber: string | null;
  reservedPitch: string | null;
  pitchId: string | null;
  note: string;
  balanceDue: number;
  autoBalance: boolean;
  zeroAndArriveUsed?: boolean;
  customer: Customer | null;
  primaryVehicleBooking: BookingFormVehicleBooking;
  vehicleBookings: Array<BookingFormVehicleBooking>;
  bookingType: BookingType;
  disallow: boolean;
};

export const getBookingFormState = (initialBooking?: Booking): BookingForm =>
  initialBooking
    ? reactive({
        ...cloneDeep(initialBooking),
        pitchType:
          initialBooking.bookingType != "experience_freedom"
            ? initialBooking.pitchType
            : null,
        pitchNumber:
          initialBooking.bookingType != "experience_freedom"
            ? initialBooking.pitch
              ? initialBooking.pitch.name
              : initialBooking.pitchNumber ?? ""
            : null,
        reservedPitch:
          initialBooking.bookingType != "experience_freedom"
            ? initialBooking.reserved?.name ?? null
            : null,
        pitchId:
          initialBooking.bookingType == "experience_freedom"
            ? initialBooking.pitch?.id ?? null
            : null,
        startDate: initialBooking.startDate,
        endDate: initialBooking.endDate,
        arrivalTimeStart:
          initialBooking.overrideArrivalStart ?? initialBooking.arrivalStart,
        arrivalTimeEnd:
          initialBooking.overrideArrivalEnd ?? initialBooking.arrivalEnd,
        lateArrivalTimeStart:
          initialBooking.overrideLateArrivalStart ??
          initialBooking.lateArrivalStart,
        departureTimeEnd:
          initialBooking.overrideDepartureEnd ?? initialBooking.departureEnd,
        arrivalTimeStartEnabled: !!initialBooking.overrideArrivalStart,
        arrivalTimeEndEnabled: !!initialBooking.overrideArrivalEnd,
        lateArrivalTimeStartEnabled: !!initialBooking.overrideLateArrivalStart,
        departureTimeEndEnabled: !!initialBooking.overrideDepartureEnd,
        disallow: initialBooking.disallow ?? false,
        primaryVehicleBooking:
          initialBooking.primaryVehicleBooking === null
            ? {
                id: null,
                registration: "",
                isPrimary: true,
              }
            : { ...initialBooking.primaryVehicleBooking },
      })
    : reactive({
        bookingRef: "",
        startDate: DateFns.format(new Date(), "yyyy-MM-dd"),
        endDate: DateFns.format(DateFns.addDays(new Date(), 7), "yyyy-MM-dd"),
        arrivalTimeStart: null,
        arrivalTimeEnd: null,
        lateArrivalTimeStart: null,
        departureTimeEnd: null,
        arrivalTimeStartEnabled: false,
        arrivalTimeEndEnabled: false,
        lateArrivalTimeStartEnabled: false,
        departureTimeEndEnabled: false,
        siteId: useCurrentSite().id.value,
        pitchType: null,
        pitchNumber: "",
        reservedPitch: null,
        pitchId: null,
        bookingType: "default",
        note: "",
        balanceDue: 0,
        autoBalance: false,
        disallow: false,
        customer: null,
        vehicleBookings: [],
        primaryVehicleBooking: {
          id: null,
          registration: "",
          isPrimary: true,
        },
      });
